/**
 * HM-500 Modulo 3 Prenotazioni — lista filtrabile/paginata.
 *
 * SPEC v2.6 Modulo 3: tabella prenotazioni con filtri laterali, search,
 * paginazione, azioni bulk, detail drawer. Per ora UI minima: tab status
 * + search publicCode + tabella + click → drawer.
 *
 * NB: usa GET /bookings?from=&to= esistente (ritorna massimo 8h range).
 * Estesa per finestra ±30g cosi' la lista copre periodo realistico.
 */
import { toast } from '@hotel-manager/ui/components';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { PlanningItem } from '../../api/planning.js';
import { type BookingDto, fetchBookingsWindow, fetchRooms } from '../../api/rooms.js';
import { PaymentAnomalyBadge, UnpaidBadge } from '../../components/UnpaidBadge.js';
import { CheckoutModal } from '../../components/checkout/CheckoutModal.js';
import { isPendingPaidAnomaly, isUnpaid, unpaidAmount } from '../../lib/paymentStatus.js';
import { useCheckoutModal } from '../../stores/checkout.js';
import { BookingDetailDrawer } from '../planning/BookingDetailDrawer.js';
// QA sweep 2026-07-22: stili .planning-drawer-* riusati dal drawer.
import '../planning/planning.css';

type StatusFilter = 'all' | 'active' | 'past' | 'cancelled';

function dateOnly(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT');
}

function statusLabel(s: string): string {
  switch (s) {
    case 'pending':
      return 'In attesa';
    case 'confirmed':
      return 'Confermata';
    case 'checked_in':
      return 'In corso';
    case 'checked_out':
      return 'Conclusa';
    case 'cancelled':
      return 'Cancellata';
    case 'no_show':
      return 'No-show';
    default:
      return s;
  }
}

function statusBg(s: string): string {
  if (s === 'checked_in') return '#FFE5DC';
  if (s === 'confirmed') return '#DCE7F0';
  if (s === 'pending') return '#FFF3E6';
  if (s === 'cancelled' || s === 'no_show') return '#F0E8DA';
  return '#E4EDDE';
}

// QA sweep 2026-07-22: adapter BookingDto → PlanningItem per riusare il
// BookingDetailDrawer del planning (nessuna dipendenza circolare: il
// drawer importa solo api/planning types + stores/auth).
function toPlanningItem(b: BookingDto, roomCode: string | null): PlanningItem {
  return {
    id: b.id,
    publicCode: b.publicCode,
    roomId: b.roomId,
    roomCode,
    roomTypeId: b.roomTypeId,
    guestId: b.guestId || null,
    guestName: b.guestName ?? null,
    service: b.service,
    status: b.status,
    channel: b.channel,
    startAt: b.startAt,
    endAt: b.endAt,
    extraHours: b.extraHours ?? 0,
    totalAmount: b.totalAmount,
    extraAmount: b.extraSumLive ?? b.extraAmount,
    paidAmount: b.paidAmount,
    checkedInAt: b.checkedInAt ?? null,
    checkedOutAt: b.checkedOutAt ?? null,
  };
}

export function PrenotazioniPage() {
  const [filter, setFilter] = useState<StatusFilter>('active');
  const [search, setSearch] = useState('');
  // QA sweep 2026-07-22: id della prenotazione aperta nel drawer dettaglio.
  const [detailId, setDetailId] = useState<string | null>(null);

  // Rooms per mappare roomId → codice camera nel drawer e aprire il
  // CheckoutModal (stessa queryKey ['rooms'] della dashboard: cache condivisa).
  const roomsQuery = useQuery({
    queryKey: ['rooms'],
    queryFn: fetchRooms,
    staleTime: 30_000,
  });

  // Finestra ±30g rispetto a oggi.
  const range = useMemo(() => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - 30);
    const to = new Date(now);
    to.setDate(to.getDate() + 60);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const bookingsQuery = useQuery({
    queryKey: ['bookings', 'wide', range.from, range.to],
    queryFn: () => fetchBookingsWindow(range),
    staleTime: 30_000,
  });

  const allBookings: BookingDto[] = bookingsQuery.data?.bookings ?? [];

  const filtered = useMemo(() => {
    let list = allBookings;
    if (filter === 'active') {
      // ONDATA 4: "Attive" = TUTTI i pending/confirmed/checked_in,
      // indipendentemente dalle date. Un checked_in in overstay (end_at gia'
      // scaduto) e' LA prenotazione piu' attiva di tutte — il vecchio vincolo
      // `endAt >= now` lo nascondeva (stessa famiglia del fix listInRange di
      // Ondata 2: W. #2026-07-22-701 occupava la 02 ma Attive era vuota).
      list = list.filter((b) => ['pending', 'confirmed', 'checked_in'].includes(b.status));
    } else if (filter === 'past') {
      // "Concluse" = solo checked_out: gli stati live restano in Attive anche
      // a date scadute (partizione per status, nessun booking in due tab).
      list = list.filter((b) => b.status === 'checked_out');
    } else if (filter === 'cancelled') {
      list = list.filter((b) => b.status === 'cancelled' || b.status === 'no_show');
    }
    if (search.trim().length >= 2) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (b) =>
          b.publicCode.toLowerCase().includes(q) || (b.guestName?.toLowerCase() ?? '').includes(q),
      );
    }
    return [...list].sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
  }, [allBookings, filter, search]);

  return (
    <div
      className="px-6 py-6 h-full flex flex-col min-h-0 max-xl:px-3 max-xl:py-3"
      data-testid="prenotazioni-page"
    >
      {/* HM-600 iPad UX: titolo + tabs/search fissi (shrink-0), scorre solo la tabella. */}
      <div className="shrink-0">
        <h1 style={{ fontSize: 28, fontWeight: 700, color: '#3D2F24', margin: 0 }}>Prenotazioni</h1>
        <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 16px 0' }}>
          {filtered.length} prenotazioni · finestra ±30 giorni
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {(
            [
              { id: 'active', label: 'Attive' },
              { id: 'past', label: 'Concluse' },
              { id: 'cancelled', label: 'Cancellate' },
              { id: 'all', label: 'Tutte' },
            ] as const
          ).map((t) => {
            const active = filter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(t.id)}
                data-testid={`prenotazioni-tab-${t.id}`}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid',
                  borderColor: active ? '#E97B3D' : '#D4C4B0',
                  background: active ? '#FFF3E6' : '#FFF',
                  color: active ? '#C4661F' : '#3D2F24',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
          <input
            type="search"
            placeholder="Cerca codice o ospite…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="prenotazioni-search"
            style={{
              marginLeft: 'auto',
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #D4C4B0',
              fontSize: 13,
              minWidth: 240,
            }}
          />
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          border: '1px solid #E8DECF',
          borderRadius: 8,
          background: '#FFF',
        }}
        data-testid="prenotazioni-scroll"
      >
        <table
          // HM-600 iPad UX: righe/celle piu' compatte <=1280 (le celle usano
          // inline style 10px 14px: il prefisso ! di Tailwind genera
          // !important per vincere sull'inline solo sotto xl). Desktop invariato.
          className="max-xl:[&_th]:!px-2 max-xl:[&_th]:!py-1.5 max-xl:[&_td]:!px-2 max-xl:[&_td]:!py-1.5"
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}
        >
          <thead style={{ background: '#FAF7F2' }}>
            <tr>
              <th style={th}>Codice</th>
              <th style={th}>Ospite</th>
              <th style={th}>Servizio</th>
              <th style={th}>Inizio</th>
              <th style={th}>Fine</th>
              <th style={{ ...th, textAlign: 'right' }}>Totale</th>
              <th style={th}>Stato</th>
            </tr>
          </thead>
          <tbody>
            {bookingsQuery.isLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}>
                  Caricamento…
                </td>
              </tr>
            )}
            {!bookingsQuery.isLoading && filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  data-testid="prenotazioni-empty"
                  style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}
                >
                  Nessuna prenotazione corrispondente al filtro.
                </td>
              </tr>
            )}
            {filtered.map((b) => (
              // QA sweep 2026-07-22 (bug MEDIO): lo spec promette il drawer
              // ma le righe non avevano onClick. Click o Enter/Space aprono
              // il dettaglio; riga focusabile da tastiera.
              <tr
                key={b.id}
                style={{ borderTop: '1px solid #F0E8DA', cursor: 'pointer' }}
                data-testid={`prenotazioni-row-${b.id}`}
                tabIndex={0}
                onClick={() => setDetailId(b.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDetailId(b.id);
                  }
                }}
              >
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{b.publicCode}</td>
                <td style={td}>
                  <strong>{b.guestName ?? '—'}</strong>
                </td>
                <td style={td}>{b.service === 'day_use' ? 'Day Use' : 'Pernottamento'}</td>
                <td style={td}>{dateOnly(b.startAt)}</td>
                <td style={td}>{dateOnly(b.endAt)}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                  € {Number.parseFloat((b.currentTotalDue ?? b.totalAmount) as string).toFixed(2)}
                  {/* Revisione pagamenti 2026-07-22: pill "DA INCASSARE €X"
                      per ospiti in camera non (interamente) pagati. */}
                  {isUnpaid(b) && (
                    <div style={{ marginTop: 2 }}>
                      <UnpaidBadge amount={unpaidAmount(b)} />
                    </div>
                  )}
                </td>
                <td style={td}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: statusBg(b.status),
                      color: '#3D2F24',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {statusLabel(b.status)}
                  </span>
                  {/* ONDATA 2 P2a: pending PAGATO = anomalia (cascade
                      walk-in incassa ma /confirm fallisce). Il cleanup non
                      li cancella piu': pill arancione per l'operatore. */}
                  {isPendingPaidAnomaly(b) && (
                    <div style={{ marginTop: 2 }}>
                      <PaymentAnomalyBadge />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* QA sweep 2026-07-22: drawer dettaglio (riuso BookingDetailDrawer
          del planning). Azione offerta: Check-out per i booking checked_in
          (apre il CheckoutModal cluster 16.2); Modifica/Annulla restano nel
          planning — qui il drawer va in sola consultazione. */}
      {detailId &&
        (() => {
          const b = allBookings.find((x) => x.id === detailId);
          if (!b) return null;
          const room = roomsQuery.data?.rooms.find((r) => r.id === b.roomId) ?? null;
          return (
            <BookingDetailDrawer
              booking={toPlanningItem(b, room?.code ?? null)}
              onClose={() => setDetailId(null)}
              onCheckOut={() => {
                if (!room) {
                  toast.error('Camera della prenotazione non trovata, ricarica la pagina');
                  return;
                }
                useCheckoutModal.getState().open(room, b);
                setDetailId(null);
              }}
            />
          );
        })()}
      <CheckoutModal />
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const td: React.CSSProperties = {
  padding: '10px 14px',
  verticalAlign: 'middle',
};
